import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { useNotifications } from '../../lib/useNotifications';
import { NotificationPopup } from '../notifications/NotificationPopup';
import { SelecteurStatutMessagerie } from '../messagerie/SelecteurStatutMessagerie';
import { Search } from 'lucide-react';
import { BarreRecherche } from './BarreRecherche';

/** Écrans où mènent les résultats de la recherche globale. */
const ECRANS_RECHERCHE = ['adm-dossier', 'learners', 'ped-repertoire', 'com-entreprises'];

interface Props {
  onOuvrirNavigation: () => void;
}

/**
 * Barre supérieure. Le choix du périmètre de sites n'est pas ici : la maquette
 * validée le place dans la barre de sites, au sein de chaque écran concerné.
 */
export function Topbar({ onOuvrirNavigation }: Props): JSX.Element {
  const { utilisateur, peutAcceder } = useAuth();
  const [popupNotifsOuvert, setPopupNotifsOuvert] = useState(false);
  const [rechercheMobile, setRechercheMobile] = useState(false);
  // L'API décide des rubriques ; le front se contente de ne pas montrer une
  // barre qui ne mènerait à aucun écran du profil.
  const rechercheOuverte = ECRANS_RECHERCHE.some((ecran) => peutAcceder(ecran));

  const { data: dataNotifs } = useNotifications({});
  const nbNonLues = dataNotifs?.compteurs?.nonLues;
  // Notifications non lues, pas les alertes ouvertes du menu (TF-492) ; rien
  // tant que le compte n'est pas connu, jamais le chiffre de la maquette.
  const affichageBadge = nbNonLues ?? 0;

  return (
    <header className="sticky top-0 z-30 border-b border-gray-200 bg-white/80 backdrop-blur">
      <div className="flex items-center gap-2 px-4 py-3 sm:gap-4 sm:px-6 sm:py-3.5">
        <button
          type="button"
          className="-ml-2 rounded-lg p-2.5 text-codialis-navy transition hover:bg-gray-100 lg:hidden"
          onClick={onOuvrirNavigation}
          aria-label="Ouvrir la navigation"
        >
          <svg aria-hidden="true" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        {rechercheOuverte && (
          <div className="hidden max-w-2xl min-w-0 flex-1 md:block">
            <BarreRecherche />
          </div>
        )}

        <div className="ml-auto flex items-center gap-1 sm:gap-2">
          {rechercheOuverte && (
            <button
              type="button"
              onClick={() => setRechercheMobile((prec) => !prec)}
              className="rounded-lg p-2.5 text-codialis-navy transition hover:bg-gray-100 md:hidden"
              aria-label={rechercheMobile ? 'Fermer la recherche' : 'Rechercher'}
              aria-expanded={rechercheMobile}
            >
              <Search className="h-5 w-5" aria-hidden="true" />
            </button>
          )}

          {/* Tuteur IA avec point pulsant vert mint comme sur la maquette */}
          {peutAcceder('ai-tutor') && (
            <Link
              to="/ai-tutor"
              className="relative rounded-lg p-2.5 transition hover:bg-gray-100"
              title="Tuteur IA"
              aria-label="Accéder au Tuteur IA"
            >
              <svg
                aria-hidden="true"
                className="h-5 w-5 text-codialis-navy"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                />
              </svg>
              <span aria-hidden="true" className="ring-pulse absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-codialis-mint" />
            </Link>
          )}

          {/* Bouton cloche avec badge et menu déroulant interactif */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setPopupNotifsOuvert((prec) => !prec)}
              className="relative rounded-lg p-2.5 transition hover:bg-gray-100"
              title="Notifications"
              aria-label={nbNonLues ? `Notifications · ${nbNonLues} non lue(s)` : 'Notifications'}
              aria-expanded={popupNotifsOuvert}
              aria-haspopup="dialog"
            >
              <svg aria-hidden="true" className="h-5 w-5 text-codialis-navy" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                />
              </svg>
              {affichageBadge > 0 && (
                <span aria-hidden="true" className="absolute top-1 right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold text-white shadow-sm animate-pulse">
                  {affichageBadge > 99 ? '99+' : affichageBadge}
                </span>
              )}
            </button>

            <NotificationPopup
              ouvert={popupNotifsOuvert}
              onFermer={() => setPopupNotifsOuvert(false)}
            />
          </div>

          {peutAcceder('messages') && <SelecteurStatutMessagerie />}

          <div className="mx-1 hidden h-6 w-px bg-gray-200 sm:block" />
          <div className="hidden text-right sm:block">
            <div className="text-sm font-semibold leading-tight text-codialis-navy">
              {utilisateur?.prenom} {utilisateur?.nom}
            </div>
            <div className="text-[11px] text-codialis-gray">{utilisateur?.libelleProfil}</div>
          </div>
        </div>
      </div>
      {rechercheOuverte && rechercheMobile && (
        <div className="border-t border-gray-200 px-4 py-2.5 md:hidden">
          <BarreRecherche autoFocus onTerminer={() => setRechercheMobile(false)} />
        </div>
      )}
    </header>
  );
}

